/**
 * Stripe Checkout Modal with embedded Payment Element
 *
 * Renders an in-app payment form using Stripe's Payment Element.
 * Styled to match SkillSnap's dark theme via Stripe appearance API.
 */
import { useState, useEffect, useRef } from 'react';
import { loadStripe, Appearance } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, Loader2, CreditCard, AlertCircle, CheckCircle } from 'lucide-react';
import { api, PricingPlan } from '../services/api';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// Stripe appearance API — match SkillSnap dark theme
const appearance: Appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#6366f1',
    colorBackground: '#1e1e2e',
    colorText: '#e2e8f0',
    colorDanger: '#ef4444',
    fontFamily: 'Inter, system-ui, sans-serif',
    borderRadius: '8px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#2a2a3e',
      border: '1px solid #3f3f5a',
      color: '#e2e8f0',
    },
    '.Input:focus': {
      borderColor: '#6366f1',
      boxShadow: '0 0 0 1px #6366f1',
    },
    '.Label': {
      color: '#94a3b8',
    },
  },
};

type PriceTier = 'standard' | 'snooze';

interface StripeCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultTier?: PriceTier;
}

export function StripeCheckoutModal({ isOpen, onClose, onSuccess, defaultTier = 'standard' }: StripeCheckoutModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<PriceTier>(defaultTier);
  const [tierChanged, setTierChanged] = useState(false);
  const [freePlan, setFreePlan] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  const [standardPlan, setStandardPlan] = useState<PricingPlan | null>(null);
  const [snoozePlan, setSnoozePlan] = useState<PricingPlan | null>(null);
  const [pricingError, setPricingError] = useState<string | null>(null);

  useEscapeClose(() => onClose(), isOpen);
  useFocusTrap(modalRef, isOpen);

  useEffect(() => {
    if (isOpen) {
      setClientSecret(null);
      setError(null);
      setTierChanged(false);
      setFreePlan(false);
      setSelectedTier(defaultTier);
      setStandardPlan(null);
      setSnoozePlan(null);
      setPricingError(null);
      api.getPublicPricing().then(({ pricing }) => {
        const std = pricing?.plans?.standard;
        const hib = pricing?.plans?.hibernate;
        if (!std || !hib) {
          setPricingError('Pricing data is incomplete. Please try again later.');
          return;
        }
        setStandardPlan(std);
        setSnoozePlan(hib);
      }).catch(() => {
        setPricingError('Failed to load pricing. Please check your connection and try again.');
      });
    }
  }, [isOpen, defaultTier]);

  const pricingLoaded = !!(standardPlan && snoozePlan);

  async function createSubscription() {
    setLoading(true);
    setError(null);
    setTierChanged(false);
    setFreePlan(false);
    try {
      const result = await api.createSubscription(selectedTier);
      if (result.free_plan) {
        setFreePlan(true);
        setLoading(false);
        return;
      }
      if (result.tier_changed) {
        setTierChanged(true);
        setLoading(false);
        // Tier was switched server-side, refresh preferences
        setTimeout(() => onSuccess(), 1500);
        return;
      }
      if (result.clientSecret) {
        setClientSecret(result.clientSecret);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create subscription. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  if (!stripePromise) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-[#1e1e2e] rounded-lg shadow-xl w-full max-w-md p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-200 mb-2">Stripe Not Configured</h2>
          <p className="text-sm text-gray-400 mb-4">Payment processing is not yet available.</p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-700 text-gray-200 rounded-lg hover:bg-gray-600">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" role="dialog" aria-modal="true" aria-label="Stripe Checkout">
      <div ref={modalRef} className="bg-[#1e1e2e] rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-lg font-semibold text-gray-200">
            {clientSecret ? 'Payment Details' : 'Choose Your Plan'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-700 rounded text-gray-400" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {freePlan && (
            <div className="flex flex-col items-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
              <p className="text-gray-200 font-medium">You have a free plan!</p>
              <p className="text-sm text-gray-400 mt-2">No payment needed. Enjoy full access.</p>
            </div>
          )}

          {tierChanged && (
            <div className="flex flex-col items-center py-8">
              <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
              <p className="text-gray-200 font-medium">Plan Updated!</p>
              <p className="text-sm text-gray-400 mt-2">Your subscription tier has been changed.</p>
            </div>
          )}

          {!clientSecret && !freePlan && !tierChanged && pricingError && (
            <div className="flex flex-col items-center py-8">
              <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
              <p className="text-gray-200 font-medium">Couldn't load plans</p>
              <p className="text-sm text-gray-400 mt-2 text-center">{pricingError}</p>
            </div>
          )}

          {!clientSecret && !freePlan && !tierChanged && !pricingError && !pricingLoaded && (
            <div className="flex flex-col items-center py-12">
              <Loader2 className="w-8 h-8 text-indigo-400 animate-spin mb-3" />
              <p className="text-sm text-gray-400">Loading plans…</p>
            </div>
          )}

          {!clientSecret && !freePlan && !tierChanged && pricingLoaded && standardPlan && snoozePlan && (
            <div className="space-y-4">
              {/* Tier Selection */}
              <div className="space-y-3">
                <button
                  onClick={() => setSelectedTier('standard')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                    selectedTier === 'standard'
                      ? 'border-indigo-500 bg-indigo-500/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-gray-200 font-medium">{standardPlan.name}</h3>
                      <p className="text-sm text-gray-400 mt-1">{standardPlan.tagline}</p>
                    </div>
                    <div className="text-right flex-shrink-0 w-32">
                      <span className="text-xl font-bold text-gray-200">{standardPlan.display_price}</span>
                      <span className="text-sm text-gray-400">{standardPlan.period_label}</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setSelectedTier('snooze')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                    selectedTier === 'snooze'
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <div className="flex items-center justify-between gap-6">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-gray-200 font-medium">{snoozePlan.name}</h3>
                      <p className="text-sm text-gray-400 mt-1">{snoozePlan.tagline}</p>
                    </div>
                    <div className="text-right flex-shrink-0 w-32">
                      <span className="text-xl font-bold text-gray-200">{snoozePlan.display_price}</span>
                      <span className="text-sm text-gray-400">{snoozePlan.period_label}</span>
                    </div>
                  </div>
                </button>
              </div>

              {error && (
                <div className="flex items-center p-3 bg-red-500/10 text-red-400 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}

              <button
                onClick={createSubscription}
                disabled={loading}
                className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center font-medium"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-5 h-5 mr-2" />
                )}
                {loading ? 'Setting up...' : 'Continue to Payment'}
              </button>
            </div>
          )}

          {clientSecret && (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret, appearance }}
            >
              <CheckoutForm onSuccess={onSuccess} onError={setError} />
              {error && (
                <div className="flex items-center p-3 bg-red-500/10 text-red-400 rounded-lg text-sm mt-4">
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  {error}
                </div>
              )}
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}


function CheckoutForm({ onSuccess, onError }: { onSuccess: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [succeeded, setSucceeded] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    onError('');

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/?checkout=success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message || 'Payment failed. Please try again.');
      setProcessing(false);
    } else {
      setSucceeded(true);
      setProcessing(false);
      // Brief delay to show success before closing
      setTimeout(() => onSuccess(), 1500);
    }
  }

  if (succeeded) {
    return (
      <div className="flex flex-col items-center py-8">
        <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
        <p className="text-gray-200 font-medium">Payment Successful!</p>
        <p className="text-sm text-gray-400 mt-2">Your subscription is now active.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center font-medium"
      >
        {processing ? (
          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
        ) : (
          <CreditCard className="w-5 h-5 mr-2" />
        )}
        {processing ? 'Processing...' : 'Subscribe Now'}
      </button>
    </form>
  );
}
